import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { PAUSE_REASONS } from '@/constants/pauseReasons';
import { pauseOption } from '@/lib/timer/interaction/pauseIntent';
import { Loader2 } from 'lucide-react';
import { TicketProvenanceBadge } from '@/components/tickets/TicketProvenanceBadge';

interface PauseReasonBadgeProps {
  ticketId: string;
  pauseReason: string;
  onReasonChanged?: (newReason: string) => void;
}

/** A technician's Stage D answer on an unresolved pause of this ticket. */
interface TechnicianAnswer { detail: string; note: string | null }

/**
 * The technicians' own answers ("Why are you stopping work?") on this ticket's
 * unresolved pauses, or [] when the pause predates Stage D.
 */
async function readTechnicianAnswers(ticketId: string): Promise<TechnicianAnswer[]> {
  const { data, error } = await (supabase as unknown as {
    from: (t: string) => {
      select: (c: string) => {
        eq: (k: string, v: string) => {
          not: (k: string, op: string, v: null) => {
            is: (k: string, v: null) => { is: (k: string, v: null) => Promise<{ data: unknown; error: unknown }> };
          };
        };
      };
    };
  }).from('ticket_pause_sessions')
    .select('pause_detail, pause_note')
    .eq('ticket_id', ticketId)
    .not('pause_intent', 'is', null)
    .is('resumed_at', null)
    .is('resolved_at', null);
  if (error) throw error;
  return ((data ?? []) as Array<{ pause_detail: string; pause_note: string | null }>)
    .map((r) => ({ detail: r.pause_detail, note: r.pause_note }));
}

export const PauseReasonBadge = ({ ticketId, pauseReason, onReasonChanged }: PauseReasonBadgeProps) => {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  // Stage D: null = not read yet. A technician's answer is shown, never edited.
  const [answers, setAnswers] = useState<TechnicianAnswer[] | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let live = true;
    setAnswers(null);
    readTechnicianAnswers(ticketId)
      .then((a) => { if (live) setAnswers(a); })
      // Fail closed: if we cannot tell, do not offer an edit.
      .catch(() => { if (live) setAnswers([{ detail: '', note: null }]); });
    return () => { live = false; };
  }, [isOpen, ticketId]);

  const handleReasonSelect = async (newReason: string) => {
    if (newReason === pauseReason) {
      setIsOpen(false);
      return;
    }

    setIsUpdating(true);
    try {
      // CSI-I2-A6 SAFE — INTENTIONAL companion-metadata-only write.
      //
      // This badge edits `pause_reason` on an ALREADY paused ticket.
      // `pause_reason` is a free-text companion column (NOT in
      // `ALLOWED_SCHEDULE_FIELDS`). The handler does not change
      // `status`, `timer_status`, `paused_at`, `paused_by`, or
      // `current_session_id`. It does not open, close, or otherwise
      // mutate `ticket_timer_sessions`. It does not write any
      // scheduling-allowlist column. There is no lifecycle transition
      // occurring — the ticket's pause state is unchanged.
      //
      // Routing this through `mutateTicketSchedule` is structurally
      // impossible: the canonical helper rejects empty schedule-field
      // payloads, and `pause_reason` is not allowlisted. Routing
      // through `mutateTicketTimerState` would require inventing a new
      // named transition (`editPauseReason`), which would expand the
      // contract surface for a single caller without corresponding
      // invariant payoff — the canonical pause invariants
      // (`status='Paused'`, `timer_status='paused'`, non-empty
      // `pause_reason`) are PRESERVED by this edit, not transitioned.
      //
      // Stage D: offered ONLY for a pre-Stage-D pause (no technician answer).
      // A technician's answer is historical truth; the database refuses this
      // write while one is unresolved (trg_ticket_pause_reason_is_the_technicians).
      const { error } = await supabase
        .from('tickets')
        .update({ pause_reason: newReason })
        .eq('id', ticketId);

      if (error) throw error;

      toast({
        title: 'Pause reason updated',
        description: `Changed to: ${newReason}`,
      });

      onReasonChanged?.(newReason);
      setIsOpen(false);
    } catch (error: unknown) {
      toast({
        title: 'Failed to update',
        description: error instanceof Error ? error.message : String((error as { message?: string })?.message ?? error),
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const renderBody = () => {
    if (isUpdating || answers === null) {
      return (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      );
    }
    if (answers.length > 0) {
      return (
        <div className="space-y-2" data-testid="pause-reason-technician-answer">
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            Why the technician stopped <TicketProvenanceBadge factGroupId="ticket.pauseReason" />
          </p>
          {answers.map((a, i) => (
            <div key={i} className="text-sm">
              <p>{pauseOption(a.detail)?.label ?? pauseReason}</p>
              {a.note && <p className="text-xs text-muted-foreground">“{a.note}”</p>}
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            This is the technician's answer and can't be changed. Respond from the ticket or the notification.
          </p>
        </div>
      );
    }
    return (
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground mb-2">Change pause reason</p>
        {PAUSE_REASONS.map((reason) => (
          <Button
            key={reason}
            variant={reason === pauseReason ? "secondary" : "ghost"}
            size="sm"
            className="w-full justify-start text-left h-8"
            onClick={() => handleReasonSelect(reason)}
          >
            {reason}
          </Button>
        ))}
      </div>
    );
  };

  // Core-100 R4 W3 (CORE-AD F): the hold reason is a person's statement — its evidence class sits beside it.
  return (
    <span className="inline-flex items-center gap-1" data-testid="pause-reason-with-provenance">
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex"
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((v) => !v);
          }}
        >
          <Badge
            variant="outline"
            className="text-xs bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800 cursor-pointer hover:bg-orange-500/20 transition-colors"
          >
            ⏸ {pauseReason}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        {renderBody()}
      </PopoverContent>
    </Popover>
    <TicketProvenanceBadge factGroupId="ticket.pauseReason" />
    </span>
  );
};
